import React, {useEffect, useRef, useState} from 'react';
import './App.scss';
import {WelcomeScreen} from "./Components/WelcomeScreen";
import {GameScreen} from "./Components/GameScreen";
import {Route, Routes} from 'react-router';
import {InfoScreen} from "./Components/InfoScreen";
import {ResultScreen} from "./Components/ResultScreen";
import {Quiz} from "./models/Quiz";
import {fetchQuiz} from "./services/QuizService";
import {prefetchSounds, SoundMap} from "./services/soundCache";
import {todayKey} from "./utils/date";
import {unlockAudio} from "./services/audioContext";

type GameStage = 'welcome' | 'playing';

function App() {
    const [gameStage, setGameStage] = useState<GameStage>('welcome');
    /** undefined = loading, null = no quiz available today. */
    const [quiz, setQuiz] = useState<Quiz | null | undefined>(undefined);
    const [sounds, setSounds] = useState<SoundMap>(new Map());
    const buttonClickAudioRef = useRef<HTMLAudioElement | null>(null);

    useEffect(() => {
        let cancelled = false;
        fetchQuiz(todayKey())
            .then(async (todaysQuiz) => {
                if (cancelled) {
                    return;
                }
                setQuiz(todaysQuiz);
                if (todaysQuiz) {
                    // Prefetch every sound up front so play survives a spotty connection.
                    const prefetched = await prefetchSounds(todaysQuiz);
                    if (!cancelled) {
                        setSounds(prefetched);
                    }
                }
            })
            .catch((e) => {
                console.error('failed to load today\'s quiz', e);
                if (!cancelled) {
                    setQuiz(null);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const startPlaying = () => {
        setGameStage('playing');
    }

    const stopPlaying = () => {
        setGameStage('welcome');
    }

    const buttonClick = (requestedAction: () => void) => {
        // Must run synchronously inside the tap: unlocks Web Audio on iOS so the
        // next question's sound can play without another tap.
        unlockAudio();
        const audio = buttonClickAudioRef.current;
        if (audio) {
            audio.play()
                .then(() => setTimeout(requestedAction, 300))
                // If the click sound can't play (autoplay policy, missing
                // file), the game must still proceed.
                .catch(() => requestedAction());
        } else {
            requestedAction();
        }
    }

    return (
        <>
            <Routes>
                <Route path="/" element={
                    <div className="App">
                        <audio preload={'auto'} ref={buttonClickAudioRef} src={'/sounds/double-click-metallic-SBA-300130466.mp3'}/>
                        {(gameStage === 'welcome' || !quiz) ?
                            <WelcomeScreen quiz={quiz} startPlaying={startPlaying} buttonClick={buttonClick}/> :
                            <GameScreen quiz={quiz} sounds={sounds} buttonClick={buttonClick} stopPlaying={stopPlaying}/>
                        }
                    </div>
                }/>
                <Route path="/info" element={
                    <div className="App"><InfoScreen/></div>
                }/>
                <Route path="/result" element={
                    <div className="App"><ResultScreen/></div>
                }/>
            </Routes>
        </>
    )
}

export default App;
